"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import type { ComparadorDisplayView } from "@/lib/comparador-displays";

const ROTATING_QUERIES = [
  "Jitomate Saladet",
  "Gansito 50 grs",
  "Coca Cola 600 ml",
  "Pan Bimbo 620 grs",
  "Papel Higiénico Pétalo",
  "Ferrero Rocher",
  "7501020565935",
  "7500478047666",
  "7500435257985",
  "7501058642608",
];

/** Shape used when an admin-managed banner is injected as a slide. */
export interface ExtraHeroSlide {
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  imageUrl: string;
  /** Full CTA URL — clicking the slide takes the user here. */
  href: string;
  /** Optional Tailwind gradient classes; falls back to a default. */
  bg?: string;
}

/** Temporada o campaña activa (Navidad, Buen Fin…), desde `bannerSeasons()`. */
export interface SeasonHeroSlide {
  eyebrow: string;
  title: string;
  subtitle: string;
  href: string;
  ctaLabel: string;
  /** Clases Tailwind del degradado de la temporada. */
  bg: string;
  /** Emoji grande de fondo (las temporadas no traen foto). */
  glyph: string;
}

const FALLBACK_BGS = [
  "from-primary via-blue-600 to-indigo-700",
  "from-emerald-500 via-teal-600 to-cyan-700",
  "from-rose-500 via-orange-500 to-yellow-500",
  "from-violet-600 via-fuchsia-600 to-pink-600",
];

const DEFAULT_BG = FALLBACK_BGS[0];

/**
 * Renderiza el título de un display. `[[texto]]` marca el resaltado con el
 * bloque de color del diseño; el resto se pinta como texto plano, así un
 * display administrable nunca puede inyectar HTML.
 */
function renderTitle(title: string) {
  return title.split(/(\[\[[^\]]*\]\])/g).map((chunk, index) => {
    const highlighted = chunk.match(/^\[\[([^\]]*)\]\]$/);
    if (!highlighted) return <span key={index}>{chunk}</span>;
    return (
      <span key={index} className="bg-yellow-300 text-blue-900 px-2 rounded-lg">
        {highlighted[1]}
      </span>
    );
  });
}

interface Slide {
  eyebrow: string;
  title: React.ReactNode;
  subtitle: string;
  cta: { label: string; href: string } | null;
  image: string;
  imageMobile: string | null;
  imageAlt: string;
  bg: string;
  glyph?: string;
}

function toSlide(display: ComparadorDisplayView, index: number): Slide {
  return {
    eyebrow: display.eyebrow ?? "",
    title: renderTitle(display.title),
    subtitle: display.subtitle ?? "",
    cta:
      display.href && display.ctaLabel
        ? { label: display.ctaLabel, href: display.href }
        : null,
    image: display.imageUrl ?? "",
    imageMobile: display.imageMobileUrl,
    imageAlt: display.imageAlt ?? "",
    bg: display.bgColor?.trim() || FALLBACK_BGS[index % FALLBACK_BGS.length],
  };
}

export function ComparadorHeroBanner({
  displays,
  extraSlides,
  seasonSlides,
}: {
  /** Displays administrables (posición COMPARADOR_HERO). */
  displays?: ComparadorDisplayView[];
  /** Creatividades patrocinadas: van al frente de la rotación. */
  extraSlides?: ExtraHeroSlide[];
  /** Temporadas y campañas activas: van después de lo patrocinado. */
  seasonSlides?: SeasonHeroSlide[];
} = {}) {
  const [idx, setIdx] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Admin-managed slides (via HOME_COMPARADOR_SLIDER) take precedence and
  // sit at the front of the rotation. The editorial defaults stay as a
  // fallback so the hero never collapses when no banner is configured.
  const sponsoredSlides: Slide[] = (extraSlides ?? []).map((s, i) => ({
    eyebrow: s.eyebrow ?? "Patrocinado",
    title: <>{s.title}</>,
    subtitle: s.subtitle ?? "",
    cta: { label: "Ver más", href: s.href },
    image: s.imageUrl,
    imageMobile: null,
    imageAlt: s.title,
    bg: s.bg ?? FALLBACK_BGS[i % FALLBACK_BGS.length],
  }));
  const seasonal: Slide[] = (seasonSlides ?? []).map((s) => ({
    eyebrow: s.eyebrow,
    title: <>{s.title}</>,
    subtitle: s.subtitle,
    cta: { label: s.ctaLabel, href: s.href },
    image: "",
    imageMobile: null,
    imageAlt: "",
    bg: s.bg,
    glyph: s.glyph,
  }));
  const editorialSlides: Slide[] = (displays ?? []).map(toSlide);
  const slides: Slide[] = [...sponsoredSlides, ...seasonal, ...editorialSlides];

  // Auto rotate
  useEffect(() => {
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, 5500);
    return () => window.clearInterval(id);
  }, [slides.length]);

  // Parallax: scroll listener
  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setScrollY(-rect.top);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Sin displays configurados el comparador NO se rompe: se conserva el
  // buscador flotante, que es la función real de este bloque.
  const slide = slides[idx] ?? slides[0];
  if (!slide) {
    return (
      <div ref={containerRef} className="relative pb-12">
        <div className="rounded-3xl bg-linear-to-br from-primary via-blue-600 to-indigo-700 text-white" style={{ minHeight: "180px" }} />
        <FloatingSearch />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
    <div
      className={`relative overflow-hidden rounded-3xl bg-linear-to-br ${slide.bg} text-white transition-[background] duration-700`}
      style={{ minHeight: "440px" }}
    >
      {/* Parallax background images */}
      <div className="absolute inset-0">
        {slides.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === idx ? 1 : 0 }}
          >
            {s.glyph && (
              <div
                aria-hidden
                className="absolute -right-6 top-4 select-none text-[11rem] leading-none opacity-25 sm:right-10 sm:top-8 sm:text-[16rem]"
                style={{ transform: `translate3d(0, ${scrollY * 0.2}px, 0)` }}
              >
                {s.glyph}
              </div>
            )}
            <div
              className="absolute inset-0 will-change-transform sm:hidden"
              role={s.imageAlt ? "img" : undefined}
              aria-label={s.imageAlt || undefined}
              style={{
                backgroundImage: s.imageMobile ?? s.image ? `url(${s.imageMobile ?? s.image})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                transform: `translate3d(0, ${scrollY * 0.35}px, 0) scale(1.15)`,
                opacity: 0.35,
              }}
            />
            <div
              className="absolute inset-0 will-change-transform hidden sm:block"
              role={s.imageAlt ? "img" : undefined}
              aria-label={s.imageAlt || undefined}
              style={{
                backgroundImage: s.image ? `url(${s.image})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                transform: `translate3d(0, ${scrollY * 0.35}px, 0) scale(1.15)`,
                opacity: 0.35,
              }}
            />
          </div>
        ))}
        {/* Gradient overlay */}
        <div className={`absolute inset-0 bg-linear-to-br ${slide.bg} mix-blend-multiply opacity-70`} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      {/* Decorative blobs (parallax slower) */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        aria-hidden
        style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
      >
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-md h-112 rounded-full bg-yellow-200/20 blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative px-6 sm:px-12 pt-10 sm:pt-14 pb-28 sm:pb-32">
        <div
          className="max-w-3xl"
          style={{ transform: `translate3d(0, ${scrollY * -0.08}px, 0)` }}
        >
          {slide.eyebrow && (
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold mb-4 border border-white/20">
              <Sparkles className="w-4 h-4 text-yellow-300" aria-hidden />
              {slide.eyebrow}
            </div>
          )}
          {/* h2: el H1 de /comparador es fijo y con la keyword ("Comparador de
              precios…"); los slides rotan por temporada. */}
          <h2 className="text-3xl sm:text-5xl font-black leading-[1.05] tracking-tight">
            {slide.title}
          </h2>
          <p className="mt-4 text-sm sm:text-base text-white/90 max-w-xl">{slide.subtitle}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {slide.cta && (
              <Link
                href={slide.cta.href}
                className="bg-white text-primary px-4 py-2 rounded-lg text-sm font-bold hover:bg-white/90 transition-colors shadow-lg"
              >
                {slide.cta.label}
              </Link>
            )}
            <Link
              href="/comparador?sort=trending"
              className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-semibold hover:bg-white/25 transition-colors border border-white/20"
            >
              Más buscados
            </Link>
          </div>
        </div>
      </div>

      {/* Slide controls */}
      {slides.length > 1 && (
      <button
        type="button"
        onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)}
        aria-label="Anterior"
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/25 transition-colors z-10"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      )}
      {slides.length > 1 && (
      <button
        type="button"
        onClick={() => setIdx((i) => (i + 1) % slides.length)}
        aria-label="Siguiente"
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/25 transition-colors z-10"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      )}

      {/* Dots */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-24 sm:bottom-28 flex items-center gap-1.5 z-10" hidden={slides.length <= 1}>
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Slide ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

    </div>
      {/* Floating search — sibling of the inner overflow-hidden banner so it isn't clipped */}
      <FloatingSearch />
    </div>
  );
}

function FloatingSearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [animated, setAnimated] = useState("");
  const [queryIdx, setQueryIdx] = useState(0);
  const [phase, setPhase] = useState<"typing" | "pause" | "deleting">("typing");

  useEffect(() => {
    const current = ROTATING_QUERIES[queryIdx];
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === "typing") {
      if (animated.length < current.length) {
        timeout = setTimeout(() => setAnimated(current.slice(0, animated.length + 1)), 60);
      } else {
        timeout = setTimeout(() => setPhase("pause"), 1400);
      }
    } else if (phase === "pause") {
      timeout = setTimeout(() => setPhase("deleting"), 600);
    } else {
      if (animated.length > 0) {
        timeout = setTimeout(() => setAnimated(animated.slice(0, -1)), 30);
      } else {
        setQueryIdx((i) => (i + 1) % ROTATING_QUERIES.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(timeout);
  }, [animated, phase, queryIdx]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    router.push(`/comparador?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="absolute left-4 right-4 sm:left-12 sm:right-12 -bottom-8 z-20">
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl bg-background/95 backdrop-blur-xl shadow-2xl border border-white/40 p-2 sm:p-3 flex items-center gap-2 sm:gap-3"
      >
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Search className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 relative">
          <div className="text-[10px] sm:text-xs text-muted-foreground font-semibold uppercase tracking-wider">
            Buscar producto
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={animated}
            aria-label="Buscar producto"
            className="w-full bg-transparent text-sm sm:text-lg font-bold text-foreground placeholder:text-foreground/60 focus:outline-hidden"
          />
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded-md font-mono shrink-0">
          ⌘ K
        </div>
      </form>
    </div>
  );
}
